import type { CSSProperties } from "react";
import { Eyebrow, Icon, Reveal, Section } from "@/ds";
import { skills, skillsSection, tools } from "@/content/portfolio";

/**
 * Tile imagery, mirroring the template's `skillImageCut` / `skillImageTone` /
 * `skillImageHeight` props. The template ships with the cut set to `hidden`,
 * so the tiles render as text-only — change these two constants to bring the
 * photography back.
 */
const SKILL_IMAGE_CUT: "hidden" | "full-bleed" | "corner-notch" | "inset-square" = "hidden";
const SKILL_IMAGE_TONE: keyof typeof TONE_FILTERS = "monochrome";
const SKILL_IMAGE_HEIGHT = 190;

const TONE_FILTERS = {
  monochrome: "grayscale(1) contrast(1.05) brightness(.85)",
  "high-contrast-mono": "grayscale(1) contrast(1.45) brightness(.7)",
  "muted-colour": "saturate(.5) brightness(.8)",
} as const;

function tileImageStyle(): CSSProperties | null {
  if (SKILL_IMAGE_CUT === "hidden") return null;

  const base: CSSProperties = {
    height: `${SKILL_IMAGE_HEIGHT}px`,
    objectFit: "cover",
    filter: TONE_FILTERS[SKILL_IMAGE_TONE],
    display: "block",
  };

  if (SKILL_IMAGE_CUT === "inset-square") {
    return { ...base, width: "100%", margin: "0 0 6px" };
  }
  if (SKILL_IMAGE_CUT === "corner-notch") {
    return {
      ...base,
      width: "calc(100% + 56px)",
      margin: "-32px -28px 2px",
      clipPath: "polygon(0 0, 68% 0, 68% 34%, 100% 34%, 100% 100%, 0 100%)",
    };
  }
  return { ...base, width: "calc(100% + 56px)", margin: "-32px -28px 2px" };
}

export function Skills() {
  const imgStyle = tileImageStyle();

  return (
    <Section
      id="skills"
      tone="ink"
      className="scroll-mt-[var(--header-h)]"
      eyebrow={<Eyebrow>{skillsSection.eyebrow}</Eyebrow>}
      title={skillsSection.headline}
      titleClassName="text-display-2 max-w-[22ch] text-on-ink"
      headerClassName="mb-[56px]"
    >
      {/* Hairline grid: the 1px gap and matching border are the rules themselves. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-px bg-border-on-ink border border-border-on-ink">
        {skills.map((skill, i) => (
          <Reveal
            key={skill.title}
            index={i}
            className="bg-surface-ink px-[28px] py-6 flex flex-col gap-[14px] overflow-hidden transition-colors duration-160 ease-out hover:bg-surface-ink-hover"
          >
            {imgStyle && <img src={skill.src} alt="" style={imgStyle} />}
            <Icon name={skill.icon} size={18} className="text-on-ink opacity-80" />
            <span className="text-[17px] font-semibold text-on-ink">{skill.title}</span>
            <p className="text-body-sm text-on-ink-body">{skill.description}</p>
          </Reveal>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6 font-label text-label uppercase text-on-ink-body">
        <span className="text-on-ink-muted mr-2">{skillsSection.toolsLabel}</span>
        {tools.map((tool) => (
          <span
            key={tool.label}
            className="inline-flex items-center gap-[7px] border border-border-on-ink rounded-pill px-[13px] py-[6px] transition-[border-color,color,background-color,transform] duration-300 ease-out hover:border-on-ink hover:text-on-ink hover:bg-surface-ink-hover hover:-translate-y-[2px]"
          >
            <Icon name={tool.icon} size={13} className="opacity-65" />
            {tool.label}
          </span>
        ))}
      </div>
    </Section>
  );
}
